import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  DeepPartial,
  EventPayload,
  IdVerification,
  PatientProfileType,
  States,
  // TrackCustomer,
  // TrackPage,
  PageEventPayload,
  PromoBanner,
  GetNextPlaceParams,
  TrackPage,
  Languages,
} from "../types";
import { BASE_URL, TAG } from "../constants";
import { getSupabaseSession } from "src/lib/supabase/supabaseClient";

export const patientApi = createApi({
  reducerPath: "patientApi",
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL,
    mode: "cors",
    prepareHeaders: async (headers) => {
      const session = await getSupabaseSession();

      if (session?.access_token != null) {
        headers.set("Authorization", `Bearer ${session.access_token}`);
      }

      return headers;
    },
  }),
  tagTypes: Object.values(TAG),
  endpoints: (builder) => ({
    createMe: builder.mutation<
      PatientProfileType,
      {
        profile_id: string;
        referred_by_code?: string;
        metadata?: Record<string, unknown>;
        profile?: {
          language?: Languages;
        };
      }
    >({
      query: (body) => ({
        url: `/api/patient`,
        method: "POST",
        body,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;

        dispatch(
          patientApi.util.updateQueryData("getMe", undefined, () => data)
        );
      },
    }),
    getMe: builder.query<PatientProfileType, void>({
      query: () => ({
        method: "GET",
        url: "/api/patient/me",
      }),
      providesTags: [TAG.USER],
    }),
    updateMe: builder.mutation<
      PatientProfileType,
      DeepPartial<PatientProfileType>
    >({
      query: (body) => ({
        method: "PUT",
        url: "/api/patient/me",
        body,
      }),
      invalidatesTags: [TAG.USER, TAG.NAVIGATION, TAG.MESSAGES],
    }),
    fireEvent: builder.mutation<void, EventPayload>({
      query: (body) => ({
        method: "POST",
        url: `/api/patient/event`,
        body,
      }),
    }),
    firePageEvent: builder.mutation<void, PageEventPayload>({
      query: (body) => ({
        method: "POST",
        url: `/api/patient/page-event`,
        body,
      }),
    }),
    // states
    getStates: builder.query<States[], void>({
      query: () => ({
        method: "GET",
        url: "/api/states",
      }),
      providesTags: [TAG.STATES],
    }),
    // vouched
    crossCheckVerification: builder.mutation({
      query: (body) => ({
        method: "POST",
        url: "/api/vouched-verification/cross-check",
        body,
      }),
      invalidatesTags: [TAG.VOUCHED_VERIFICATION],
    }),
    ssnVerification: builder.mutation({
      query: (body) => ({
        method: "POST",
        url: "/api/vouched-verification/ssn",
        body,
      }),
      invalidatesTags: [TAG.VOUCHED_VERIFICATION],
    }),
    photoIdentification: builder.mutation({
      query: (body) => ({
        method: "POST",
        url: "/api/vouched-verification/photo-identification",
        body,
      }),
    }),
    // freshpaint
    trackFreshpaintEvent: builder.mutation<void, EventPayload>({
      query: (body) => ({
        method: "POST",
        url: "/api/freshpaint",
        body,
      }),
    }),
    updateIdVerification: builder.mutation<
      IdVerification,
      Partial<IdVerification>
    >({
      query: (body) => ({
        method: "PUT",
        url: `/api/patient/id-verification`,
        body,
      }),
    }),

    /**
     * Messages
     */
    //patient-navigation
    getNextPlace: builder.query<string, GetNextPlaceParams>({
      query: ({ visit_type, product_id }) => ({
        method: "GET",
        url: "/api/patient/next-place",
        responseHandler: "text",
        params: {
          visit_type,
          product_id,
        },
      }),
      providesTags: [TAG.NAVIGATION],
    }),

    trackPage: builder.mutation<void, TrackPage>({
      query: (body) => ({
        method: "POST",
        url: "/api/track/page",
        body,
      }),
    }),
    // promo banner
    getPromoBanner: builder.query<
      PromoBanner,
      { lang: string; product: string }
    >({
      query: ({ lang, product }) => ({
        method: "GET",
        url: `/api/banners/${product}/${lang}`,
      }),
    }),
  }),
});

export const {
  useCreateMeMutation,
  useGetMeQuery,
  useUpdateMeMutation,
  useGetStatesQuery,
  useCrossCheckVerificationMutation,
  useSsnVerificationMutation,
  usePhotoIdentificationMutation,
  useTrackFreshpaintEventMutation,
  useUpdateIdVerificationMutation,
  useLazyGetNextPlaceQuery,
  useTrackPageMutation,
  useFireEventMutation,
  useFirePageEventMutation,
  useGetPromoBannerQuery,
} = patientApi;
